import { useNavigate } from "@tanstack/react-router";
import {
  ControlCode,
  ControlCodes,
  getControlName,
  PartnerEvents,
  ValidPartnerEventCode,
  isIdentityProviderControl,
  isEmailControl,
  getPartnerEventName,
  getValidControlCategory,
} from "@theprelude/core";
import { Button, ExternalLinkIcon, UserIcon } from "@theprelude/ds";
import { ReactNode } from "react";
import { z } from "zod";
import {
  MissingMFADeepLink,
  PolicyHostsReducedFunctionalityDeepLink,
} from "@/components/deep-links";
import { DefaultOverlay } from "@/components/default-overlay";
import { Filter, filtersSchema } from "@/lib/filter";
import {
  controlInstanceSchema,
  validPartnerEventSchema,
} from "@/lib/validators";
import { ControlFailureEndpointsTable } from "../endpoint-tables/control-failure-endpoints-table";
import { ControlFailureUsersTable } from "../endpoint-tables/control-failure-users-table";

const overlay = "control-failures";

export const controlFailuresSchema = z.tuple([
  z.literal(overlay),
  controlInstanceSchema.extend({
    partnerEvent: validPartnerEventSchema.optional().catch(1),
    filters: filtersSchema.optional().default([]),
  }),
]);

type ControlFailuresSchema = z.infer<typeof controlFailuresSchema>[1];

export const ControlFailuresOverlay = (props: ControlFailuresSchema) => {
  const title = getTitle(props);

  const { control, partnerEvent } = props;

  const navigate = useNavigate();

  const onFilter = (filters: Filter[]) =>
    navigate({
      to: window.location.pathname,
      params: (prev) => prev,
      search: (prev) => ({
        ...prev,
        overlay: [overlay, { ...props, filters }],
      }),
    });

  const ControlFailuresTable = isIdentityProviderControl(control)
    ? ControlFailureUsersTable
    : ControlFailureEndpointsTable;

  return (
    <DefaultOverlay title={title} defaultSize={60}>
      <div className="flex h-full flex-col gap-4 p-4 pb-0">
        {partnerEvent === PartnerEvents.REDUCED_FUNCTIONALITY_MODE && (
          <ReducedFunctionalityInfo control={control} />
        )}
        {partnerEvent !== undefined && (
          <div className="flex-1 overflow-hidden">
            <ControlFailuresTable
              {...props}
              category={getValidControlCategory(control)}
              partnerEvent={partnerEvent}
              initialQuery={getInitialQuery(partnerEvent)}
              onFilter={onFilter}
            />
          </div>
        )}
      </div>
    </DefaultOverlay>
  );
};

const getInitialQuery = (
  partnerEvent: ValidPartnerEventCode,
): Record<any, any> | undefined => {
  switch (partnerEvent) {
    case PartnerEvents.MISSING_AV_POLICY: {
      return { instances: { any: { no_av_policy: true } } };
    }
    case PartnerEvents.MISSING_EDR_POLICY: {
      return { instances: { any: { no_edr_policy: true } } };
    }
    case PartnerEvents.MISSING_MFA: {
      return { instances: { any: { missing_mfa: true } } };
    }
    case PartnerEvents.MISSING_SCAN: {
      return { instances: { any: { missing_scan: true } } };
    }
    case PartnerEvents.NO_VULN_MANAGER: {
      return { instances: { any: { has_agent: false } } };
    }
    case PartnerEvents.OUT_OF_DATE_SCAN: {
      return { instances: { any: { out_of_date_scan: true } } };
    }
    case PartnerEvents.REDUCED_FUNCTIONALITY_MODE: {
      return { instances: { any: { reduced_functionality_mode: true } } };
    }
    case PartnerEvents.USER_MISSING_ASSET_MANAGER:
    case PartnerEvents.USER_MISSING_EDR:
    case PartnerEvents.USER_MISSING_VULN_MANAGER: {
      return {
        missing_asset_manager: true,
        missing_edr: true,
        missing_vuln_manager: true,
      };
    }
    default: {
      return {};
    }
  }
};

const getTitle = ({ control, partnerEvent }: ControlFailuresSchema) => {
  const controlName = getControlName(control);

  const resourceType = isIdentityProviderControl(control)
    ? "Users"
    : isEmailControl(control)
      ? "Inboxes"
      : "Endpoints";

  const eventName =
    partnerEvent === PartnerEvents.USER_MISSING_ASSET_MANAGER ||
    partnerEvent === PartnerEvents.USER_MISSING_EDR ||
    partnerEvent === PartnerEvents.USER_MISSING_VULN_MANAGER
      ? "No Registered Devices"
      : getPartnerEventName(partnerEvent ?? PartnerEvents.INVALID);

  return `${controlName} ${resourceType} with ${eventName}`;
};

const ReducedFunctionalityInfo: React.FC<{ control: ControlCode }> = ({
  control,
}) => {
  const controlName = getControlName(control);

  if (control === ControlCodes.CROWDSTRIKE) {
    return (
      <div className="flex flex-col items-start gap-4">
        <div>
          Reduced functionality mode (RFM) is a safe mode for the sensor that
          indicates a compatibility or configuration issue. The sensor is still
          connecting to the cloud, but is not fully functional.
        </div>
        <div className="flex flex-col">
          <Link href="https://falcon.us-2.crowdstrike.com/documentation/page/ecc97e75/falcon-sensor-for-windows-deployment#t66e9575">
            Windows Reduced Functionality Mode
          </Link>
          <Link href="https://falcon.us-2.crowdstrike.com/documentation/page/ea6bf997/falcon-sensor-for-linux-modes#r1167a77">
            Linux Reduced Functionality Mode
          </Link>
          <Link href="https://falcon.us-2.crowdstrike.com/documentation/page/e261a9b7/falcon-sensor-for-mac-deployment#pb0ee694">
            macOS Reduced Functionality Mode
          </Link>
        </div>
        <PolicyHostsReducedFunctionalityDeepLink control={control}>
          <Button
            intent="pill"
            size="tiny"
            icon={<ExternalLinkIcon className="text-secondary-foreground" />}
          >
            View endpoints in {getControlName(control)}
          </Button>
        </PolicyHostsReducedFunctionalityDeepLink>
      </div>
    );
  }

  if (control === ControlCodes.DEFENDER) {
    return (
      <div className="flex flex-col items-start gap-4">
        <div>
          When {controlName} is running in passive mode, it will not take
          preventative action against discovered threats. Files will be scanned
          and threats will be reported, but {controlName} is not treated as the
          primary antivirus on the endpoint.
        </div>
        <div className="flex flex-col">
          <Link href="https://learn.microsoft.com/en-us/defender-endpoint/microsoft-defender-antivirus-compatibility">
            {controlName} Antivirus compatibility with other security products
          </Link>
          <Link href="https://learn.microsoft.com/en-us/defender-endpoint/microsoft-defender-antivirus-windows#comparing-active-mode-passive-mode-and-disabled-mode">
            Comparing active mode, passive mode, and disabled mode
          </Link>
        </div>
      </div>
    );
  }

  if (control === ControlCodes.SENTINELONE) {
    return (
      <div className="flex flex-col items-start gap-4">
        <div>
          Each {controlName} agent has a status reported in the Management
          Console that tracks non-optimal conditions.
        </div>
        <div className="flex flex-col">
          <Link href="https://usea1-partners.sentinelone.net/docs/en/agent-operational-state-overview.html">
            Agent Operational State
          </Link>
        </div>
      </div>
    );
  }

  if (control === ControlCodes.OKTA) {
    return (
      <div className="flex flex-col items-start gap-4">
        <div>
          Multi-Factor Authentication (MFA) is a security process that requires
          users to provide more than one way to verify their identity to access
          an account.
        </div>
        <div className="flex flex-col">
          <Link href="https://www.okta.com/resources/whitepaper/8-steps-for-effectively-deploying-mfa/">
            8 Steps for Effectively Deploying MFA
          </Link>
        </div>
        <MissingMFADeepLink control={control}>
          <Button
            intent="pill"
            size="tiny"
            icon={<UserIcon className="text-secondary-foreground" />}
          >
            View MFA Configuration
          </Button>
        </MissingMFADeepLink>
      </div>
    );
  }

  return null;
};

const Link = ({ children, href }: { children: ReactNode; href: string }) => (
  <a
    href={href}
    target="_blank"
    rel="noreferrer"
    className="flex items-center gap-1"
  >
    <ExternalLinkIcon />
    <span className="underline underline-offset-2">{children}</span>
  </a>
);
